import { Dispatch } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import { fetchUserContext } from '@zola-helpers/client/dist/es/redux/user/userActions';
import { getCookie } from '@zola-helpers/client/dist/es/util/storage';
import { OnboardingRequest, onboardUser } from '@zola-helpers/client/dist/es/api/onboard';

import { useOnboardingContext } from '../context';
import type { CoreAccountDetailsValues } from '../types';
import { formatCoreAccountDetails } from './submitFormatters';
import useRecaptchaV2 from '../../../hooks/useRecaptchaV2';

const useSignupHelpers = ({
  onAccountCreation,
  appDispatch,
  recaptchaContainer,
}: {
  onAccountCreation?: () => void;
  appDispatch: Dispatch<unknown>;
  recaptchaContainer: HTMLElement | null;
}): ((values: CoreAccountDetailsValues) => Promise<unknown>) => {
  const { onboardingState, setOnboardingState } = useOnboardingContext();
  const recaptcha = useRecaptchaV2({ recaptchaContainer });
  const { initialUserContext } = onboardingState;
  const isAutomatedTest = getCookie('automatedTest') === 'true';
  const isExistingAccount = !_isEmpty(initialUserContext) && !initialUserContext.is_guest;

  const handleOnboardError = (error?: { response: { message?: string } }) => {
    const headline =
      error?.response?.message ||
      'There was a problem processing your request. Please try again or contact customer support for assistance.';
    appDispatch(
      toastsActions.negative({
        headline,
      })
    );
    throw new Error(headline);
  };

  const afterSignup = () => {
    if (onAccountCreation) onAccountCreation();
    return fetchUserContext().then((uc) => {
      const { step, sectionStepsLength } = onboardingState;
      const isLastStep = step === sectionStepsLength - 1;
      if (!isLastStep) {
        // Don't update on the last step because it can reset form field before later async actions.
        setOnboardingState({ ...onboardingState, currentUserContext: uc });
      }
    });
  };

  const onSignup = async (values: CoreAccountDetailsValues) => {
    if (isExistingAccount) {
      const onboardRequest = formatCoreAccountDetails({ ...values, isExistingAccount });
      return onboardUser(onboardRequest as OnboardingRequest)
        .then(afterSignup)
        .catch(handleOnboardError);
    }
    try {
      return await new Promise((resolve, reject) => {
        const THIRTY_SECONDS = 30000;
        const recaptchaTimeout = setTimeout(() => {
          reject(new Error('Verification timed out. Please try again.'));
        }, THIRTY_SECONDS);

        const onboardUserWithCaptchaToken = (captchaToken: string) => {
          const onboardRequest = formatCoreAccountDetails({
            ...values,
            isExistingAccount,
            captchaToken,
          });
          return onboardUser(onboardRequest as OnboardingRequest)
            .then(afterSignup)
            .then(resolve)
            .catch((error) => {
              const headline =
                error?.response?.message ||
                'There was a problem processing your request. Please try again or contact customer support for assistance.';
              throw new Error(headline);
            });
        };

        if (recaptcha) {
          recaptcha
            .executeAsync()
            .then((captchaToken) => {
              if (recaptchaTimeout) {
                window.clearTimeout(recaptchaTimeout);
              }
              recaptcha.reset();
              if (captchaToken) {
                return onboardUserWithCaptchaToken(captchaToken);
              }
              throw new Error('captcha failed');
            })
            .catch(reject);
        } else if (isAutomatedTest) {
          // Skipping recaptcha for automatedTest
          onboardUserWithCaptchaToken('').catch(reject);
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch ({ message }: any) {
      recaptcha?.reset();

      appDispatch(
        toastsActions.negative({
          headline: message as string,
          autoDismissInSeconds: 2,
        })
      );
      return Promise.reject();
    }
  };

  return onSignup;
};

export default useSignupHelpers;
